import CtaArrow from "@/components/svg/CtaArrow";
import SubHeader from "@/components/SubHeader";
import { isFilled } from "@prismicio/client";
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next";
import { PrismicRichText } from "@prismicio/react";

const richTextBase = {
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  hyperlink: ({ children, node }) => (
    <a href={node.data.url} className="underline underline-offset-2">
      {children}
    </a>
  ),
};

const sectionTitleComponents = {
  ...richTextBase,
  heading2: ({ children }) => (
    <h2 className="h2-n lg:dk max-w-[456px] text-blanc">{children}</h2>
  ),
  heading3: ({ children }) => (
    <h3 className="h2-n lg:dk max-w-[456px] text-blanc">{children}</h3>
  ),
};

const sectionTextComponents = {
  ...richTextBase,
  paragraph: ({ children }) => (
    <p className="p-lg-n text-blanc">{children}</p>
  ),
};

const itemTitleComponents = {
  ...richTextBase,
  heading3: ({ children }) => <h3 className="h3-n lg:dk text-blanc">{children}</h3>,
  heading4: ({ children }) => <h3 className="h3-n lg:dk text-blanc">{children}</h3>,
};

const itemTextComponents = {
  ...richTextBase,
  paragraph: ({ children }) => (
    <p className="p-base-n text-blanc not-last:mb-5">{children}</p>
  ),
};

/**
 * @param {{ item: import("@prismicio/client").Content.ServicesDisplaySliceDefaultPrimaryServicesItem; reversed: boolean }} props
 */
const Service = ({ item, reversed }) => {
  const hasImage = isFilled.image(item.image);

  return (
    <div
      className={`flex flex-col gap-6 lg:items-start lg:gap-20 ${reversed ? "lg:flex-row-reverse" : "lg:flex-row"}`}
    >
      {hasImage && (
        <div className="relative h-[240px] w-full shrink-0 overflow-hidden rounded-[10px] md:h-[300px] lg:h-auto lg:w-[400px] lg:self-stretch">
          <PrismicNextImage
            field={item.image}
            fallbackAlt=""
            className="absolute inset-0 h-full w-full object-cover"
            sizes="(max-width: 1024px) 100vw, 400px"
          />
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-5 lg:gap-[30px] lg:py-5">
        {isFilled.richText(item.title) && (
          <PrismicRichText field={item.title} components={itemTitleComponents} />
        )}
        {isFilled.richText(item.text) && (
          <div>
            <PrismicRichText field={item.text} components={itemTextComponents} />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * @param {import("@prismicio/react").SliceComponentProps<import("@prismicio/client").Content.ServicesDisplaySliceDefault>} props
 */
const ServicesDisplayDefault = ({ slice }) => {
  const { sub_header, title, text, services, cta_title, cta_text, cta } =
    slice.primary;

  const filledServices = Array.isArray(services)
    ? services.filter(
        (item) =>
          isFilled.richText(item.title) ||
          isFilled.richText(item.text) ||
          isFilled.image(item.image),
      )
    : [];

  const hasIntro =
    isFilled.keyText(sub_header) ||
    isFilled.richText(title) ||
    isFilled.richText(text);
  const hasCta =
    isFilled.richText(cta_title) ||
    isFilled.richText(cta_text) ||
    isFilled.link(cta);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-bleu padding-x py-10 lg:pb-[90px] lg:pt-20"
    >
      <div className="framing flex flex-col gap-10 lg:gap-15">
        {hasIntro && (
          <div className="flex max-w-[623px] flex-col gap-1.5">
            {isFilled.keyText(sub_header) && <SubHeader text={sub_header} />}
            {isFilled.richText(title) && (
              <PrismicRichText field={title} components={sectionTitleComponents} />
            )}
            {isFilled.richText(text) && (
              <PrismicRichText field={text} components={sectionTextComponents} />
            )}
          </div>
        )}

        {filledServices.length > 0 && (
          <div className="flex flex-col gap-10 lg:gap-[62px]">
            {filledServices.map((item, index) => (
              <Service
                key={`service-${index}`}
                item={item}
                reversed={index % 2 === 1}
              />
            ))}
          </div>
        )}

        {hasCta && (
          <div className="flex flex-col gap-10">
            {(isFilled.richText(cta_title) || isFilled.richText(cta_text)) && (
              <div className="flex max-w-[620px] flex-col gap-5">
                {isFilled.richText(cta_title) && (
                  <PrismicRichText
                    field={cta_title}
                    components={itemTitleComponents}
                  />
                )}
                {isFilled.richText(cta_text) && (
                  <div>
                    <PrismicRichText
                      field={cta_text}
                      components={itemTextComponents}
                    />
                  </div>
                )}
              </div>
            )}
            {isFilled.link(cta) && (
              <PrismicNextLink
                prefetch={false}
                field={cta}
                className="cta cta-text w-full bg-rouge text-blanc md:w-fit"
              >
                {cta.text}
                <CtaArrow className="text-blanc" />
              </PrismicNextLink>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

/**
 * @typedef {import("@prismicio/client").Content.ServicesDisplaySlice} ServicesDisplaySlice
 * @typedef {import("@prismicio/react").SliceComponentProps<ServicesDisplaySlice>} ServicesDisplayProps
 * @type {import("react").FC<ServicesDisplayProps>}
 */
const ServicesDisplay = ({ slice }) => {
  switch (slice.variation) {
    default:
      return <ServicesDisplayDefault slice={slice} />;
  }
};

export default ServicesDisplay;
